// coachesApi.js
import { createApi } from "@reduxjs/toolkit/query/react";
import { customFetchBase } from "../customFetchBase";

export const coachesApi = createApi({
  reducerPath: "coachesApi",
  baseQuery: customFetchBase({ url: "coach" }),
  endpoints: (builder) => ({
    // Get all coaches with filters
    getCoaches: builder.query({
      query: (params = {}) => {
        const {
          page = 1,
          limit = 10,
          search,
          mainSpecialty,
          area,
          gender,
          isActive,
          isDiscount,
          minRating,
          sort = "-rating_stats.average_rating,-rating_stats.total_reviews",
        } = params;

        const searchParams = new URLSearchParams();

        searchParams.set("page", page);
        searchParams.set("limit", limit);
        searchParams.set("sort", sort);

        if (search) searchParams.set("q", search);
        if (mainSpecialty) searchParams.set("mainSpecialty[eq]", mainSpecialty);
        if (area) searchParams.set("area[eq]", area);
        if (gender) searchParams.set("gender", gender);
        if (isActive !== undefined) searchParams.set("isActive", isActive);
        if (isDiscount !== undefined)
          searchParams.set("isDiscount", isDiscount);
        if (minRating) searchParams.set("minRating", minRating);

        return {
          url: `?${searchParams.toString()}`,
          method: "GET",
        };
      },
    }),


    // Get single coach by ID
    getCoachById: builder.query({
      query: (id) => ({
        url: `/${id}`,
        method: "GET",
      }),
    }),

    // Create new coach
    createCoach: builder.mutation({
      query: (data) => ({
        url: "/",
        method: "POST",
        body: data,
      }),
    }),

    // Update coach
    updateCoach: builder.mutation({
      query: ({ id, data }) => ({
        url: `/${id}`,
        method: "PATCH",
        body: data,
      }),
    }),

    // Toggle coach active status
    toggleCoachStatus: builder.mutation({
      query: (id) => ({
        url: `/status/${id}`,
        method: "PATCH",
      }),
    }),

    // Get top coaches
    getTopCoaches: builder.query({
      query: ({ limit = 5 } = {}) => `/top?limit=${limit}`,
    }),

    // Get discounted coaches
    getDiscountedCoaches: builder.query({
      query: ({ page = 1, limit = 10 } = {}) =>
        `/discounted?page=${page}&limit=${limit}`,
    }),

    // Get coaches by specialty
    getCoachesBySpecialty: builder.query({
      query: ({ specialtyId, page = 1, limit = 10 }) =>
        `/specialty/${specialtyId}?page=${page}&limit=${limit}`,
    }),

    // Get coaches by area
    getCoachesByArea: builder.query({
      query: ({ areaId, page = 1, limit = 10 }) =>
        `/area/${areaId}?page=${page}&limit=${limit}`,
    }),

    // Get coaches statistics
    getCoachesStats: builder.query({
      query: () => "/stats",
    }),

    // Update pricing
    updatePricing: builder.mutation({
      query: ({ id, pricing }) => ({
        url: `/${id}/pricing`,
        method: "PUT",
        body: { pricing },
      }),
    }),

    // Update social media
    updateSocialMedia: builder.mutation({
      query: ({ id, socialMedia }) => ({
        url: `/${id}/social`,
        method: "PUT",
        body: { socialMedia },
      }),
    }),

    // Increment coach view
    incrementCoachView: builder.mutation({
      query: (id) => ({
        url: `/${id}/view`,
        method: "PATCH",
      }),
    }),

    // Soft delete coach
    softDeleteCoach: builder.mutation({
      query: (id) => ({
        url: `/${id}/soft`,
        method: "DELETE",
      }),
    }),

    // Permanent delete coach (admin only)
    deleteCoach: builder.mutation({
      query: (id) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
    }),

    // Bulk create coaches
    bulkCreateCoaches: builder.mutation({
      query: (coachesData) => ({
        url: "/bulk",
        method: "POST",
        body: { coaches: coachesData },
      }),
    }),

    // Bulk deactivate coaches
    bulkDeactivateCoaches: builder.mutation({
      query: (coachIds) => ({
        url: "/bulk/deactivate",
        method: "POST",
        body: { coachIds },
      }),
    }),
  }),
});

// Export hooks
export const {
  // Queries
  useGetCoachesQuery,
  useGetCoachByIdQuery,
  useGetTopCoachesQuery,
  useGetDiscountedCoachesQuery,
  useGetCoachesBySpecialtyQuery,
  useGetCoachesByAreaQuery,
  useGetCoachesStatsQuery,

  useCreateCoachMutation,
  useUpdateCoachMutation,
  useToggleCoachStatusMutation,
  useUpdatePricingMutation,
  useUpdateSocialMediaMutation,
  useIncrementCoachViewMutation,
  useSoftDeleteCoachMutation,
  useDeleteCoachMutation,
  useBulkCreateCoachesMutation,
  useBulkDeactivateCoachesMutation,
} = coachesApi;
